<template>
    <div class="scope-page">
        <!-- 搜索栏 + 操作按钮 -->
        <div class="rightHeader">
            <v-search :formContent='formContent' :SearchForm='SearchForm' :inline='true' :optareBtn='optareBtn'
                      @resetFn='resetFn' @searchFn='getListFn' @addFn='addFn' @DelFn='DelFn'>
            </v-search>
        </div>

        <!-- 列表 -->
        <div class='componentPage'>
            <el-table
                v-loading="loading"
                :data="tableData"
                stripe
                style="width: 100%"
                :height="tableHeight"
                :empty-text="$t('account_redesign.no_data')"
                @selection-change="handleSelectionChange"
            >
                <el-table-column type="selection" width="55" />
                <el-table-column :label="$t('account_redesign.scope_name')" prop="name" min-width="160" show-overflow-tooltip></el-table-column>
                <el-table-column :label="$t('account_redesign.company')" min-width="160" show-overflow-tooltip>
                    <template #default="{ row }">
                        {{ row.customerName || '-' }}
                    </template>
                </el-table-column>
                <el-table-column :label="$t('account_redesign.organization')" min-width="220">
                    <template #default="{ row }">
                        <div class="tree-cell" v-if="row.sceneTree && row.sceneTree.length > 0 && getFilteredTree(row.sceneTree, row.sceneIds).length > 0">
                            <el-tree
                                :data="getFilteredTree(row.sceneTree, row.sceneIds)"
                                :props="treeProps"
                                :expand-on-click-node="true"
                                :default-expand-all="true"
                                node-key="sceneId"
                            ></el-tree>
                        </div>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('account_redesign.member_count')" min-width="200">
                    <template #default="{ row }">
                        <div v-if="getMemberNames(row.memberNames).length > 0" class="member-tags">
                            <el-tag v-for="(name, idx) in getMemberNames(row.memberNames)" :key="idx" size="small" type="info" class="member-tag">{{ name }}</el-tag>
                        </div>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('account_redesign.actions')" width="120" align="center">
                    <template #default="{ row }">
                        <el-button type="text" icon="el-icon-edit" size="small" :title="$t('account_redesign.edit')" @click="openEditDialog(row)"></el-button>
                        <el-button type="text" icon="el-icon-delete" size="small" :title="$t('account_redesign.delete')" class="delete-btn" @click="handleDelete(row)"></el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页 -->
            <Pagination class='pagination' :total='total' :page.sync='SearchForm.iDisplayStart'
                        :limit.sync='SearchForm.iDisplayLength' @pagination='getListFn'></Pagination>
        </div>

        <!-- 新增/编辑范围弹窗 -->
        <modal :title="dialogTitle" :visible.sync="dialogVisible" width="600px" :close-on-click-modal="false">
            <el-form ref="scopeForm" :model="scopeForm" :rules="scopeRules" label-width="140px" @submit.native.prevent>
                <el-form-item :label="$t('account_redesign.company')" prop="customerId">
                    <el-select
                        v-if="isPlatformUser"
                        v-model="scopeForm.customerId"
                        :placeholder="$t('public.please')"
                        style="width: 300px"
                        @change="handleCompanyChange"
                    >
                        <el-option
                            v-for="item in companyOptions"
                            :key="item.value"
                            :value="item.value"
                            :label="item.label"
                        ></el-option>
                    </el-select>
                    <el-input
                        v-else
                        :value="currentCompanyName"
                        disabled
                        style="width: 300px"
                    ></el-input>
                </el-form-item>
                <el-form-item v-if="scopeForm.customerId" :label="$t('account_redesign.organization')" prop="sceneIds">
                    <div class="tree-select-wrapper" v-loading="treeLoading">
                        <el-tree
                            :data="sceneTree"
                            :props="treeProps"
                            show-checkbox
                            node-key="sceneId"
                            ref="scopeTree"
                            :default-checked-keys="defaultCheckedKeys"
                            :default-expand-all="false"
                            :default-expanded-keys="defaultExpandedKeys"
                            @check="handleTreeCheck"
                        ></el-tree>
                    </div>
                </el-form-item>
                <el-form-item :label="$t('account_redesign.scope_name')" prop="name">
                    <el-input v-model.trim="scopeForm.name" :placeholder="$t('account_redesign.scope_name')" maxlength="50" style="width: 300px"></el-input>
                </el-form-item>
                <el-form-item :label="$t('account_redesign.description')" prop="description">
                    <el-input type="textarea" v-model="scopeForm.description" :placeholder="$t('account_redesign.description')" maxlength="200" :rows="3" show-word-limit style="width: 300px"></el-input>
                </el-form-item>
            </el-form>
            <div slot="footer">
                <el-button type="primary" @click="dialogVisible = false">{{ $t('account_redesign.close') }}</el-button>
                <el-button type="primary" :loading="submitLoading" @click="handleSubmit">{{ $t('account_redesign.ok') }}</el-button>
            </div>
        </modal>
    </div>
</template>

<script>
import {
    listScopes,
    createScope,
    updateScope,
    deleteScope
} from '@/api/modules/account.js'
import { accountStructure } from '@/api/modules/userInfo.js'
import { shareScope } from '@/api/modules/public.js'

export default {
    name: 'ScopeManagement',
    data() {
        return {
            loading: false,
            tableData: [],
            selectedRows: [],
            tableHeight: 'calc(100% - 60px)',
            // 搜索
            SearchForm: {
                name: '',
                iDisplayStart: 0,
                iDisplayLength: 15
            },
            // 企业列表
            customerList: [],
            // 组织架构树
            sceneTree: [],
            treeProps: {
                label: 'label',
                children: 'item'
            },
            treeLoading: false,
            // 弹窗
            dialogVisible: false,
            submitLoading: false,
            isEdit: false,
            scopeForm: {
                id: null,
                name: '',
                customerId: '',
                sceneIds: [],
                description: ''
            },
            defaultCheckedKeys: [],
            defaultExpandedKeys: [],
            // 分页
            total: 0
        }
    },
    computed: {
        isPlatformUser() {
            return this.$store.state.type === 2 || this.$store.state.type === 3
        },
        companyOptions() {
            return (this.customerList || []).filter(item => item.value !== null && item.value !== '-1' && item.value !== '-2')
        },
        currentCompanyName() {
            const found = this.customerList.find(item => item.value === this.scopeForm.customerId)
            return found ? found.label : ''
        },
        dialogTitle() {
            return this.isEdit
                ? this.$t('account_redesign.edit_scope')
                : this.$t('account_redesign.new_scope')
        },
        formContent() {
            return [
                { id: 1, text: 'account_redesign.scope_name', type: 'input', paramsName: 'name', isChooze: true }
            ]
        },
        optareBtn() {
            return [
                { id: 1, icon: '', text: 'public.btn_new', method: 'addFn' },
                { id: 2, icon: '', text: 'public.btn_delete', method: 'DelFn' }
            ]
        },
        scopeRules() {
            return {
                name: [
                    { required: true, message: this.$t('account_redesign.scope_name_required'), trigger: 'blur' },
                    { max: 50, message: this.$t('validate.len_50'), trigger: 'blur' }
                ],
                sceneIds: [
                    { required: true, validator: this.validateSceneIds, trigger: 'change' }
                ],
                customerId: [
                    { required: true, message: this.$t('account_redesign.company_required'), trigger: 'change' }
                ]
            }
        }
    },
    mounted() {
        this.fetchCustomers().then(() => {
            this.fetchData()
        })
    },
    methods: {
        validateSceneIds(rule, value, callback) {
            if (!value || value.length === 0) {
                callback(new Error(this.$t('account_redesign.organization_required')))
            } else {
                callback()
            }
        },
        async fetchCustomers() {
            try {
                const { result } = await shareScope({ mode: 'customer', local: 1 })
                this.customerList = (result || []).map(item => {
                    return {
                        value: item.id,
                        label: item.id === '-1' || item.id === '-2' ? this.$t('public.' + item.name) : item.name
                    }
                })
                if (!this.isPlatformUser && this.customerList.length > 0) {
                    const realCustomer = this.customerList.find(item => item.value !== null && item.value !== '-1' && item.value !== '-2')
                    if (realCustomer) {
                        this.scopeForm.customerId = realCustomer.value
                    }
                }
            } catch (e) {
                console.error('fetch customers error:', e)
                this.customerList = []
            }
        },
        async fetchData() {
            this.loading = true
            try {
                const customerId = this.isPlatformUser ? '' : (this.scopeForm.customerId || '')
                const res = await listScopes({
                    customerId,
                    start: this.SearchForm.iDisplayStart,
                    size: this.SearchForm.iDisplayLength,
                    name: this.SearchForm.name || undefined
                })
                if (res && res.state && res.state.code === 200) {
                    this.tableData = res.result || []
                    this.total = res.pageInfo ? res.pageInfo.total : 0
                } else {
                    this.tableData = []
                    this.total = 0
                }
            } catch (e) {
                console.error('fetch scopes error:', e)
                this.tableData = []
                this.total = 0
            } finally {
                this.loading = false
            }
        },
        /**
         * 根据 customerId 加载该企业的组织架构树
         * type=0 时后端会根据 customerId 获取 customer.scopeId，返回对应树结构
         */
        async fetchScenes(customerId) {
            if (!customerId) {
                this.sceneTree = []
                return
            }
            this.treeLoading = true
            try {
                const res = await accountStructure({
                    type: 0,
                    isEdit: 0,
                    customerId: customerId
                })
                const tree = (res && res.result && res.result.item) || []
                this.sceneTree = tree
            } catch (e) {
                console.error('fetch scenes error:', e)
                this.sceneTree = []
            } finally {
                this.treeLoading = false
            }
        },
        handleCompanyChange() {
            this.scopeForm.sceneIds = []
            if (this.$refs.scopeTree) {
                this.$refs.scopeTree.setCheckedKeys([])
            }
            this.$refs.scopeForm.validateField('customerId')
            // 根据选择的 Company 重新加载组织架构树
            this.fetchScenes(this.scopeForm.customerId)
        },
        /** 将逗号分隔的成员名称字符串拆分为数组 */
        getMemberNames(namesStr) {
            if (!namesStr) return []
            return namesStr.split(',').map(s => s.trim()).filter(s => s)
        },
        /** 根据逗号分隔的 sceneIds 字符串，过滤出仅包含选中节点的子树（保留祖先路径） */
        getFilteredTree(sceneTree, sceneIdsStr) {
            if (!sceneTree || !sceneIdsStr) return []
            const ids = sceneIdsStr.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
            if (ids.length === 0) return []
            return this.filterTreeByIds(sceneTree, ids)
        },
        /** 递归过滤树：保留 id 在 ids 中的节点及其祖先路径 */
        filterTreeByIds(nodes, ids) {
            const result = []
            if (!nodes) return result
            nodes.forEach(node => {
                const children = this.filterTreeByIds(node.item, ids)
                const isMatch = ids.includes(node.sceneId)
                if (isMatch || children.length > 0) {
                    result.push({ ...node, item: children })
                }
            })
            return result
        },
        resetFn() {
            this.SearchForm = { name: '', iDisplayStart: 0, iDisplayLength: 15 }
            this.fetchData()
        },
        getListFn() {
            this.fetchData()
        },
        handleSelectionChange(rows) {
            this.selectedRows = rows
        },
        /** 新增 */
        addFn() {
            this.isEdit = false
            this.scopeForm = {
                id: null,
                name: '',
                customerId: this.isPlatformUser ? '' : this.scopeForm.customerId,
                sceneIds: [],
                description: ''
            }
            this.defaultCheckedKeys = []
            this.defaultExpandedKeys = []
            this.sceneTree = []
            this.dialogVisible = true
            this.$nextTick(() => {
                this.$refs.scopeForm && this.$refs.scopeForm.clearValidate()
                this.$refs.scopeTree && this.$refs.scopeTree.setCheckedKeys([])
                // 非平台用户已有固定 customerId，直接加载树
                if (!this.isPlatformUser && this.scopeForm.customerId) {
                    this.fetchScenes(this.scopeForm.customerId)
                }
            })
        },
        /** 编辑 */
        openEditDialog(row) {
            this.isEdit = true
            this.scopeForm = {
                id: row.id,
                name: row.name || '',
                customerId: row.customerId || '',
                sceneIds: row.sceneIds ? row.sceneIds.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)) : [],
                description: row.description || ''
            }
            this.defaultCheckedKeys = [...this.scopeForm.sceneIds]
            this.defaultExpandedKeys = [...this.scopeForm.sceneIds]
            this.sceneTree = []
            this.dialogVisible = true
            this.$nextTick(() => {
                this.$refs.scopeForm && this.$refs.scopeForm.clearValidate()
                // 编辑时根据 customerId 加载树
                if (this.scopeForm.customerId) {
                    this.fetchScenes(this.scopeForm.customerId)
                }
            })
        },
        /** 树选择变化 — 只收集全选节点（不包含半选父节点） */
        handleTreeCheck() {
            const checked = this.$refs.scopeTree.getCheckedNodes()
            const ids = checked.map(item => item.sceneId).filter(id => id != null && !isNaN(id))
            this.scopeForm.sceneIds = ids
            this.$refs.scopeForm.validateField('sceneIds')
        },
        /** 保存 */
        async handleSubmit() {
            try {
                await this.$refs.scopeForm.validate()
            } catch (e) {
                return
            }
            this.submitLoading = true
            try {
                const payload = {
                    name: this.scopeForm.name,
                    sceneIds: this.scopeForm.sceneIds,
                    description: this.scopeForm.description,
                    customerId: this.scopeForm.customerId
                }
                let res
                if (this.isEdit) {
                    payload.id = this.scopeForm.id
                    res = await updateScope(payload)
                } else {
                    res = await createScope(payload)
                }
                if (res && res.state && res.state.code === 200) {
                    this.$message.success(this.$t('account_redesign.save') + ' OK')
                    this.dialogVisible = false
                    this.fetchData()
                } else {
                    const msg = (res && res.state && res.state.msg) || 'Error'
                    this.$message.error(msg)
                }
            } catch (e) {
                console.error('submit scope error:', e)
                this.$message.error(e.message || 'Error')
            } finally {
                this.submitLoading = false
            }
        },
        /** 批量删除 */
        DelFn() {
            if (this.selectedRows.length === 0) {
                this.$message.warning(this.$t('public.least_one_data'))
                return
            }
            this.$confirm(this.$t('account_redesign.tip_delete_scope'), this.$t('public.prompt'), {
                confirmButtonText: this.$t('public.btn_confirm'),
                cancelButtonText: this.$t('public.btn_cancel'),
                type: 'warning'
            }).then(() => {
                const promises = this.selectedRows.map(row => deleteScope(row.id))
                Promise.all(promises).then(() => {
                    this.$message.success(this.$t('public.tip_del_succ').toString())
                    this.fetchData()
                }).catch(e => {
                    console.error('batch delete error:', e)
                })
            }).catch(() => {})
        },
        /** 单条删除 */
        handleDelete(row) {
            this.$confirm(this.$t('account_redesign.tip_delete_scope'), this.$t('public.prompt'), {
                confirmButtonText: this.$t('public.btn_confirm'),
                cancelButtonText: this.$t('public.btn_cancel'),
                type: 'warning'
            }).then(() => {
                deleteScope(row.id).then(res => {
                    if (res && res.state && res.state.code === 200) {
                        this.$message.success('OK')
                        this.fetchData()
                    }
                }).catch(e => {
                    console.error('delete scope error:', e)
                })
            }).catch(() => {})
        }
    }
}
</script>

<style lang='scss' scoped>
.scope-page {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    padding: 10px 15px 0 15px;
    box-sizing: border-box;

    .rightHeader {
        position: relative;
        flex-shrink: 0;
    }

    .componentPage {
        flex: 1;
        overflow: auto;

        .tree-cell {

            ::v-deep .el-tree {
                background: transparent;
            }

            ::v-deep .el-tree-node__content {
                height: 28px;
            }
        }

        .member-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;

            .member-tag {
                margin: 0;
            }
        }

        .delete-btn {
            color: #f56c6c;
        }

        .pagination {
            padding-top: 0;
            padding-right: 15px !important;
            display: flex;
            align-items: center;
            justify-content: flex-end;
        }
    }
}

.tree-select-wrapper {
    width: 300px;
    max-height: 280px;
    overflow: auto;
    border: 1px solid #e7e7e7;
    border-radius: 4px;
    padding: 10px;
    box-sizing: border-box;
}
</style>
